import { FC } from 'react';
import { CircularProgressbarDefaultProps, RangeColor } from './CircularProgressBar.types';
import { StyledPath, StyledSvg, StyledText, StyledTrailPath } from './CircularProgressBar.styles';
import { AllieColors } from '../../common/color.types';

const VIEWBOX_CENTER_X = 50;
const VIEWBOX_CENTER_Y = 50;
const VIEWBOX_HEIGHT_HALF = 50;
const VIEWBOX_WIDTH = 100;
const VIEWBOX_HEIGHT = 100;

export const breakpointsDefault: RangeColor[] = [
  { start: 0, end: 65, color: 'error' },
  { start: 65, end: 85, color: 'warning' },
  { start: 85, end: 100, color: 'success' },
];

export const AllieCircularProgressBar: FC<CircularProgressbarDefaultProps> = ({
  styles,
  classes,
  circleRatio = 1,
  counterClockwise = false,
  className = '',
  maxValue = 100,
  minValue = 0,
  strokeWidth = 5,
  value,
  breakpoints,
  textValue,
}) => {
  const getPathRadius = () => {
    return VIEWBOX_HEIGHT_HALF - strokeWidth / 2;
  };

  // Ratio of path length to trail length, as a value between 0 and 1
  const getPathRatio = () => {
    const boundedValue = Math.min(Math.max(value, minValue), maxValue);
    return (boundedValue - minValue) / (maxValue - minValue);
  };

  const getColorStroke = (): AllieColors | undefined => {
    const breakpointsData = breakpoints || breakpointsDefault;
    return breakpointsData.find((bp) => value >= bp.start && value < bp.end)?.color;
  };

  const pathRadius = getPathRadius();
  const pathRatio = getPathRatio();

  return (
    <StyledSvg
      className={className}
      style={styles?.root}
      viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
    >
      <StyledTrailPath
        className={classes?.trail}
        counterClockwise={counterClockwise}
        dashRatio={circleRatio}
        pathRadius={pathRadius}
        strokeWidth={strokeWidth}
        style={styles?.trail}
      />

      <StyledPath
        className={classes?.path}
        counterClockwise={counterClockwise}
        dashRatio={pathRatio * circleRatio}
        pathRadius={pathRadius}
        strokeWidth={strokeWidth}
        style={styles?.path}
        strokeColor={getColorStroke()}
      />
      <StyledText
        className={classes?.text}
        mode={'md'}
        style={styles?.text}
        x={VIEWBOX_CENTER_X}
        y={VIEWBOX_CENTER_Y}
      >
        {textValue || (value > 999 ? '+999%' : `${value}%`)}
      </StyledText>
    </StyledSvg>
  );
};
